import { useMemo, useEffect, Fragment, type ReactNode } from 'react';
import { ThemeProvider, CssBaseline, GlobalStyles, alpha } from '@mui/material';
import { buildTheme } from './index';
import { getCrmBrand, getCrmPalette, applyCrmAccent, applyCrmMode, persistCrmAccent, accentFromHost, acentoAplicado } from './brand';
import { setChartMode } from '../lib/chartTheme';
import { useThemeMode } from './ThemeModeContext';
import { setFavicon, BOOSTER_FAVICON } from '../lib/favicon';
import { useAppSelector } from '../hooks/useAppStore';

// Envuelve las superficies del CRM (login + app logueada) con el theme de la
// marca Booster. El resto de la app (sitio público) usa el theme global Imperium.
// White-label (Etapa 5): SOLO el CRM logueado (whiteLabel) aplica el color del tenant.
// Las superficies públicas (landing/booster) usan SIEMPRE la marca Booster — por eso el
// default es whiteLabel=false: un visitante logueado NO debe pintar la web pública con su color.
export function CrmThemeProvider({ children, whiteLabel = false }: { children: ReactNode; whiteLabel?: boolean }) {
  const { mode } = useThemeMode();
  const branding = useAppSelector((s) => s.auth.user?.tenant?.branding);
  /**
   * El modo lo elige el usuario, sea del tenant que sea (20/08/2026).
   *
   * Antes el claro estaba cerrado a Imperium de raíz: el resto veía oscuro aunque tuviera
   * la preferencia guardada. Se abrió junto con el arreglo de los acentos del claro, que
   * estaban cableados al dorado (ver `theme/index.ts`). El botón que cambia el modo sigue
   * detrás de la flag `theme-toggle`.
   */
  const effectiveMode: 'light' | 'dark' = mode;
  const tenantColor = whiteLabel ? branding?.primaryColor : undefined;
  // Pre-login (sin branding) el color lo decide el DOMINIO: el /login de Imperium va dorado,
  // el de Booster queda emerald. Una vez logueado, manda el branding del tenant.
  const colorReal = tenantColor ?? (whiteLabel ? accentFromHost() : undefined);
  // Aplica el color a la paleta Booster (CRM_PALETTE incluida) ANTES de renderizar los hijos.
  // Si colorReal=undefined → RESETEA a la marca Booster pristina.
  //
  // ⛔ VA EL COLOR REAL DEL TENANT. El 25 de Agosto `applyCrmAccent` lo vira al celeste por su
  // cuenta, y de ese viraje deduce que hay conmemoración. Pasarle un celeste ya resuelto acá
  // le saca esa señal: la pantalla igual sale celeste —el color llega— pero se apagan la
  // banderita, el panel de la declaratoria en la home y los íconos de la barra.
  applyCrmAccent(colorReal);
  /**
   * El acento que QUEDÓ aplicado — el celeste del 25 si le tocaba, o el del tenant.
   *
   * (25/08/2026) Se lee, no se vuelve a calcular: alcanza con que la paleta y el tema de MUI
   * salgan del mismo lugar para que no puedan discrepar. Alimenta `buildTheme` y las variables
   * CSS `--brand-*`, que es como el color llega a los componentes que no leen la paleta.
   */
  const effectiveColor = acentoAplicado();
  // Ajusta los tokens de superficie (bg/text/border/shadow) al modo claro/oscuro, para que
  // los componentes que leen CRM_PALETTE directo se adapten sin tocarlos uno por uno.
  applyCrmMode(effectiveMode);
  // Modo de los charts (ApexCharts) → labels/ejes/tooltip legibles en claro y oscuro.
  setChartMode(effectiveMode);
  // Persistir solo desde el CRM, para que el próximo load lo aplique antes de que los componentes
  // capturen la paleta. localStorage es por-origen, así que el dorado de Imperium nunca filtra a
  // boostercrm.app. Las superficies públicas (whiteLabel=false) no persisten.
  // ⛔ Se persiste el color REAL del tenant, NUNCA el celeste de la conmemoración: lo guardado
  // se aplica al CARGAR el módulo `brand.ts`, así que un celeste pineado saldría el 26 —antes de
  // que este provider pueda corregirlo— y encima horneado en los componentes que capturan la
  // paleta a nivel módulo. El 25 el celeste lo vuelve a decidir `acentoConmemorativo` en cada load.
  useEffect(() => { if (whiteLabel) persistCrmAccent(colorReal); }, [colorReal, whiteLabel]);

  const theme = useMemo(() => buildTheme(effectiveMode, getCrmBrand(), effectiveColor), [effectiveMode, effectiveColor]);
  const pal          = useMemo(() => getCrmPalette(), [effectiveColor]);
  const accent       = pal.brand.golden;     // acento de marca (tenant o Booster)
  const accentStrong = pal.brand.goldenMid;

  // Pestaña del navegador: nombre de marca del tenant si tiene, si no "Booster CRM".
  useEffect(() => {
    document.title = (whiteLabel && branding?.displayName) ? `${branding.displayName} CRM` : 'Booster CRM';
    setFavicon(BOOSTER_FAVICON);
  }, [branding?.displayName, whiteLabel]);

  return (
    <ThemeProvider theme={theme}>
      {/* CssBaseline propio del CRM: sin esto el subtree hereda el CssBaseline del theme
          Imperium (AppShell) que OCULTA el scrollbar globalmente. Montarlo acá emite las
          reglas de scrollbar VISIBLE del theme Booster. Mismo patrón que PublicLayout/
          OwnerPortalLayout. (reportado por Admin Harmony: "no hay barra de scroll") */}
      <CssBaseline />
      {/* Variables CSS de marca (derivadas de la paleta) — para que los pocos
          spots que usan strings de color en sx lean la marca sin importar la
          paleta archivo por archivo. Selección de texto también en verde Booster. */}
      <GlobalStyles styles={{
        ':root': {
          '--brand-accent':        accent,
          '--brand-accent-strong': accentStrong,
          '--brand-on':            pal.brand.onAccent, // texto/ícono sobre acento sólido
          '--brand-line':          alpha(accent, 0.4),
          '--brand-tint':          alpha(accent, 0.10),
          // Texto mode-aware para spots que usan style inline (sin acceso al theme de MUI):
          // blanco en oscuro, oscuro en claro. Evita textos blancos invisibles en modo claro.
          '--text-strong':         theme.palette.text.primary,
          '--text-muted':          theme.palette.text.secondary,
        },
        '::selection':      { background: alpha(accent, 0.3), color: '#fff' },
        '::-moz-selection': { background: alpha(accent, 0.3), color: '#fff' },

        // ─── Scrollbars del CRM: ganan por ESPECIFICIDAD, no por orden ─────────
        // El CssBaseline de arriba ya pide barra visible, pero el theme raíz (marca
        // Imperium, que envuelve toda la app) pide `*::-webkit-scrollbar {display:none}`.
        // Ambos selectores tienen la MISMA especificidad, así que gana el que quede
        // último en el <head> — y eso depende del orden de montaje/re-render de los dos
        // CssBaseline. De ahí que la barra apareciera para unos y no para otros
        // (Admin Harmony antes, Cristian ahora). Prefijar con `html` sube la
        // especificidad y saca el empate de raíz.
        // Solo aplica dentro del CRM — este provider no monta en el sitio público.

        // La barra de la PÁGINA la dibujamos nosotros (<PageScrollbar/>) para que no se
        // desvanezca nunca; la nativa del documento queda oculta para no duplicarla.
        'html::-webkit-scrollbar': { display: 'none', width: 0, height: 0 },
        'html': { scrollbarWidth: 'none' },

        // Los scrollers INTERNOS (tablas, paneles, listas) sí usan la nativa, fina y
        // en el color de marca: son muchos y cambian de tamaño solos, no vale la pena
        // dibujar una barra por cada uno.
        'html *': {
          scrollbarWidth: 'thin',
          scrollbarColor: `${alpha(accent, 0.35)} transparent`,
        },
        'html ::-webkit-scrollbar': {
          display: 'block', width: 10, height: 10, background: 'transparent',
        },
        'html ::-webkit-scrollbar-track': { background: 'transparent' },
        'html ::-webkit-scrollbar-thumb': {
          background: alpha(accent, 0.30), borderRadius: 8,
          border: '3px solid transparent', backgroundClip: 'padding-box',
        },
        'html ::-webkit-scrollbar-thumb:hover': {
          background: alpha(accent, 0.5), backgroundClip: 'padding-box',
        },
        // Excepciones: los scrollers que ya traen barra dorada dibujada a mano
        // (modales vía DialogScrollbars, formulario de propiedad vía AlwaysScrollbar)
        // ocultan su nativa con estas clases. Sin esto se las devolveríamos y quedarían
        // dos barras encimadas.
        'html .__crmCustomBar::-webkit-scrollbar, html .__crmManualBar::-webkit-scrollbar': {
          display: 'none', width: 0, height: 0,
        },
        'html .__crmCustomBar, html .__crmManualBar': { scrollbarWidth: 'none' },
      }} />
      {/* key={mode}: al cambiar claro/oscuro remonta el árbol → todos los componentes
          re-leen la paleta al modo actual (los que hornean CRM_PALETTE.* en su sx no
          re-renderizan solos). Equivale a un refresh instantáneo, sin red. */}
      <Fragment key={effectiveMode}>{children}</Fragment>
    </ThemeProvider>
  );
}
